import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, MapPin, Calendar, Home } from "lucide-react";
import SEO from "@/components/SEO";
import { CTAButton, SectionLabel } from "@/components/common/Bits";
import { Reveal } from "@/components/motion/Reveal";
import { Spinner } from "@/components/common/States";
import BeforeAfterSlider from "@/components/ui/BeforeAfterSlider";
import Lightbox from "@/components/ui/Lightbox";
import { getProjectBySlug } from "@/services/api";
import { waLink, WA_MESSAGES } from "@/data/company";

export default function ProjectDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lightbox, setLightbox] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        getProjectBySlug(slug).then((p) => {
            if (!active) return;
            setProject(p);
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [slug]);

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center pt-24">
                <Spinner />
            </div>
        );
    }

    if (!project) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 pt-24 text-center">
                <h1 className="font-display text-3xl font-semibold text-brand-blue">Project not found</h1>
                <CTAButton onClick={() => navigate("/our-works")}>Back to Our Works</CTAButton>
            </div>
        );
    }

    const date = project.completionDate
        ? new Date(project.completionDate + "-01").toLocaleDateString("en-SG", { month: "long", year: "numeric" })
        : null;

    return (
        <>
            <SEO title={project.title} path={`/our-works/${slug}`} description={project.shortDescription} image={project.featuredImage} />

            <div className="bg-brand-blue pt-32 grain">
                <div className="mx-auto max-w-6xl px-6 pb-12">
                    <nav className="flex items-center gap-2 text-xs text-white/60">
                        <Link to="/our-works" className="hover:text-white">Our Works</Link>
                        <ChevronRight className="h-3.5 w-3.5" />
                        <span className="text-white">{project.title}</span>
                    </nav>
                    <SectionLabel light className="mt-6">{project.category}</SectionLabel>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl">{project.title}</h1>
                    <div className="mt-6 flex flex-wrap gap-6 text-sm text-white/70">
                        <span className="flex items-center gap-2"><Home className="h-4 w-4 text-brand-red" />{project.propertyType}</span>
                        <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-red" />{project.location}</span>
                        {date && <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-brand-red" />{date}</span>}
                    </div>
                </div>
            </div>

            <section className="py-16">
                <div className="mx-auto max-w-6xl px-6">
                    <Reveal>
                        <div className="overflow-hidden rounded-[28px] shadow-brand-lg">
                            <img src={project.featuredImage} alt={project.title} className="max-h-[560px] w-full object-cover" />
                        </div>
                    </Reveal>

                    <Reveal className="mt-12 max-w-3xl">
                        <h2 className="font-display text-2xl font-semibold text-brand-blue">Project overview</h2>
                        <p className="mt-4 text-base leading-relaxed text-brand-ink/70">{project.fullDescription}</p>
                    </Reveal>

                    {project.before && project.after && (
                        <Reveal className="mt-14">
                            <SectionLabel>The transformation</SectionLabel>
                            <div className="mt-6">
                                <BeforeAfterSlider before={project.before} after={project.after} />
                            </div>
                        </Reveal>
                    )}

                    {project.gallery?.length > 1 && (
                        <Reveal className="mt-14">
                            <SectionLabel>Gallery</SectionLabel>
                            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {project.gallery.map((img, i) => (
                                    <button key={i} onClick={() => setLightbox(img)} data-testid={`gallery-img-${i}`} className="group overflow-hidden rounded-2xl shadow-brand">
                                        <img src={img} alt={`${project.title} ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    </button>
                                ))}
                            </div>
                        </Reveal>
                    )}

                    <Reveal className="mt-16 rounded-[32px] bg-brand-surface p-10 text-center">
                        <h3 className="font-display text-2xl font-semibold text-brand-blue">Want a result like this?</h3>
                        <p className="mx-auto mt-3 max-w-lg text-brand-ink/60">Tell us about your property and we’ll help coordinate the right services.</p>
                        <div className="mt-6 flex flex-wrap justify-center gap-3">
                            <CTAButton href={waLink(WA_MESSAGES.quote)} whatsapp testid="project-whatsapp-cta">WhatsApp Us</CTAButton>
                            <CTAButton to="/contact" variant="blue" testid="project-contact-cta">Request a Quotation</CTAButton>
                        </div>
                    </Reveal>
                </div>
            </section>

            {lightbox && <Lightbox src={lightbox} onClose={() => setLightbox(null)} />}
        </>
    );
}
